import { SectinTypes } from '../actions.const';
import { getActionsNames } from './get-actions-names';
import { toPascalCase } from './to-pascal-case';

export interface GetActionsClassOptions {
  context: string;
  feature: string;
  section: SectinTypes | string;
  action: string;
}

export interface ActionWithPayload<P = void> {
  payload?: P;
  type: string;
}

export interface ActionConstructor<P = void> {
  readonly type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  new (...args: any[]): ActionWithPayload<P>;
}

export interface GetActionsClassReturnType<
  TPayload = void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TPayloadSuccessful = any
> {
  action: ActionConstructor<TPayload>;
  request: ActionConstructor<void>;
  successful: ActionConstructor<TPayloadSuccessful>;
  failure: ActionConstructor<Error>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getActionsClass<TPayload = void, TPayloadSuccessful = any>({
  context,
  feature,
  section,
  action,
}: Partial<GetActionsClassOptions>): GetActionsClassReturnType<
  TPayload,
  TPayloadSuccessful
> {
  const actionsNames = getActionsNames({
    context,
    feature,
    section,
    action,
  });

  const baseName = toPascalCase(action as string);
  class Action implements ActionWithPayload<TPayload> {
    static readonly type = actionsNames.action;
    readonly type = actionsNames.action;
    constructor(readonly payload: TPayload) {}
  }

  Object.defineProperty(Action, 'name', { value: baseName });

  class ActionRequest implements ActionWithPayload<void> {
    static readonly type = actionsNames.request;
    payload?: void | undefined;
    readonly type: string = actionsNames.request;
  }

  Object.defineProperty(ActionRequest, 'name', { value: `${baseName}Request` });

  class ActionSuccessful implements ActionWithPayload<TPayloadSuccessful> {
    static readonly type = actionsNames.successful;
    readonly type: string = actionsNames.successful;
    constructor(readonly payload: TPayloadSuccessful) {}
  }

  Object.defineProperty(ActionSuccessful, 'name', {
    value: `${baseName}Successful`,
  });

  class ActionFailure implements ActionWithPayload<Error> {
    static readonly type = actionsNames.failure;
    readonly type: string = actionsNames.failure;
    constructor(readonly payload: Error) {}
  }

  Object.defineProperty(ActionFailure, 'name', { value: `${baseName}Failure` });

  return {
    action: Action,
    request: ActionRequest,
    successful: ActionSuccessful,
    failure: ActionFailure,
  };
}
